import { z } from 'zod';

// 'free' is a display-only label for grandfathered legacy users. It never
// appears as a checkout target - the pricing page and HomePage grid both hide
// it, and nothing writes planId: 'free' server-side after the paywall cut.
//
// 'lifetime' is a one-time $99 purchase, not a subscription. Its quotas are a
// single fixed allowance that never renews (see ONE_TIME_PLANS). It is sold
// only on the mobile funnel - the desktop pricing grid and the settings billing
// grid both enumerate their plans explicitly, so lifetime never leaks into them.
//
// 'monthly' is the mobile paywall's $49 subscription. It exists as its own plan
// (rather than pointing mobile at 'starter') so that repricing the desktop
// ladder can never silently rewrite the mobile paywall again — which is exactly
// what the 2026-07-12 Starter reprice did. Like 'lifetime', it is sold only on
// the mobile funnel and is absent from PaidPlanId.
// 'lifetime_offer' is the SAME product as 'lifetime' at a lower price ($59 vs
// $79), sold on ONE surface only: the /offer exit page a buyer lands on after
// backing out of Stripe. It is a separate PlanId rather than a discount on
// 'lifetime' because the price a customer paid has to stay legible from their
// user doc forever - a coupon on the same plan id would leave the two $79
// buyers and the $59 buyers indistinguishable after the fact.
export const PlanId = z.enum(['free', 'monthly', 'starter', 'cashflow', 'scale', 'agency', 'lifetime', 'lifetime_offer']);
export type PlanId = z.infer<typeof PlanId>;

// Plans that are actually for sale as subscriptions (Pricing / HomePage grid).
// 'agency' was retired 2026-07-12 and un-retired 2026-07-24 with the legacy
// funnel revert: the restored /pricing grid lists it again, so it has to be
// sellable. Its Stripe price ("Romulus: Agency Plan", $1299/mo) was never
// archived, so nothing had to be recreated.
export const PaidPlanId = z.enum(['starter', 'cashflow', 'scale', 'agency']);
export type PaidPlanId = z.infer<typeof PaidPlanId>;

// Plans billed once rather than monthly. The single source of truth for the
// "is this a subscription?" question, which the whole Stripe layer branches on:
// one-time plans have NO stripeSubscriptionId, so every code path that infers
// "no subscription => not paying" must consult this first.
export const ONE_TIME_PLANS: readonly PlanId[] = ['lifetime', 'lifetime_offer'];

export function isOneTimePlan(planId: PlanId | undefined | null): boolean {
  return !!planId && ONE_TIME_PLANS.includes(planId);
}

export const QuotasSchema = z.object({
  leadsLimit: z.number(),
  sitesLimit: z.number(),
});
export type Quotas = z.infer<typeof QuotasSchema>;

export const UsageSchema = z.object({
  leadsUsed: z.number(),
  sitesUsed: z.number(),
});
export type Usage = z.infer<typeof UsageSchema>;

export const ConnectStatus = z.enum(['pending', 'restricted', 'active']);
export type ConnectStatus = z.infer<typeof ConnectStatus>;

export const PendingPlanChangeSchema = z.object({
  newPlanId: PlanId,
  effectiveDate: z.any(),
  scheduledAt: z.any(),
});
export type PendingPlanChange = z.infer<typeof PendingPlanChangeSchema>;

export const UserSchema = z.object({
  fullName: z.string(),
  companyName: z.string(),
  targetLocation: z.string(),
  selectedNiches: z.array(z.string()),
  onboardingCompleted: z.boolean(),
  subscriptionActive: z.boolean().default(false),
  stripeCustomerId: z.string().optional(),
  stripeSubscriptionId: z.string().optional(),
  stripeConnectAccountId: z.string().optional(),
  stripeConnectStatus: ConnectStatus.optional(),
  planId: PlanId,
  quotas: QuotasSchema,
  usage: UsageSchema,
  pendingPlanChange: PendingPlanChangeSchema.optional().nullable(),
  // --- Lifetime plan bookkeeping -------------------------------------------
  // `hasLifetime` is permanent once the $99 one-time payment lands. It is what
  // lets the subscription-reset paths (syncSubscriptionStatus,
  // customer.subscription.deleted) tell "this user never had a subscription
  // because they bought lifetime" apart from "this user's subscription lapsed".
  hasLifetime: z.boolean().optional(),
  stripeLifetimePaymentIntentId: z.string().optional(),
  // The lifetime allowance is a single bucket that never refills. When a
  // lifetime user upgrades to a monthly plan we reset `usage` to 0 (they are
  // paying for a fresh monthly bucket), so we stash the consumed lifetime
  // amount here first. If they later cancel that subscription we restore it —
  // otherwise upgrade-then-cancel would hand out a brand new 1,000 leads for
  // one month's fee, over and over.
  lifetimeUsage: UsageSchema.optional(),
  // Legacy free-tier users prior to the hard-paywall cut get grandfathered
  // access: the subscription gate in App.tsx treats grandfathered === true
  // as equivalent to subscriptionActive === true.
  grandfathered: z.boolean().optional(),
  createdAt: z.any(),
  updatedAt: z.any().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const CampaignStatus = z.enum(['active', 'paused', 'completed']);
export type CampaignStatus = z.infer<typeof CampaignStatus>;

export const CampaignSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  name: z.string(),
  niche: z.string(),
  nicheLabel: z.string().optional(),
  location: z.string(),
  isLocalCampaign: z.boolean().optional(),
  status: CampaignStatus,
  maxLeads: z.number().optional(),
  lastScrapedAt: z.any().optional(),
  createdAt: z.any(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const LeadStatus = z.enum(['new', 'contacted', 'interested', 'converted', 'rejected']);
export type LeadStatus = z.infer<typeof LeadStatus>;

export const ContactMethod = z.enum(['email', 'sms']);
export type ContactMethod = z.infer<typeof ContactMethod>;

export const GoogleReviewSchema = z.object({
  authorName: z.string(),
  rating: z.number(),
  text: z.string(),
  relativeTime: z.string().optional(),
});
export type GoogleReview = z.infer<typeof GoogleReviewSchema>;

export const LeadSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  campaignId: z.string(),
  businessName: z.string(),
  // Contact method - 'email' or 'sms'
  contactMethod: ContactMethod.optional(),
  email: z.string().nullable().optional(),
  emailConfidence: z.number().nullable().optional(),
  emailSource: z.enum(['pattern', 'manual']).nullable().optional(),
  phone: z.string().nullable().optional(),
  phoneNormalized: z.string().nullable().optional(),
  smsCapable: z.boolean().optional(), // true if phone can receive SMS
  niche: z.string(),
  location: z.string(),
  rating: z.number().optional(),
  reviewCount: z.number().optional(),
  hasWebsite: z.boolean(),
  siteGenerated: z.boolean().default(false),
  googlePlaceId: z.string(),
  businessImages: z.array(z.string()).default([]),
  googleReviews: z.array(GoogleReviewSchema).default([]),
  businessHours: z.array(z.string()).default([]),
  priceLevel: z.string().optional(),
  businessStatus: z.string().optional(),
  googleMapsUrl: z.string().optional(),
  notes: z.string().optional(),
  status: LeadStatus,
  createdAt: z.any(),
});
export type Lead = z.infer<typeof LeadSchema>;

export const SiteStatus = z.enum(['draft', 'preview', 'sent', 'sold']);
export type SiteStatus = z.infer<typeof SiteStatus>;

export const TemplateType = z.enum([
  'modern-minimal',
  'bold-corporate',
  'warm-friendly',
  'tech-startup',
  'elegant-luxury',
  'playful-creative',
  'professional-services',
  'healthcare-wellness',
  'restaurant-cafe',
  'fitness-gym',
  'real-estate',
  'automotive',
  'beauty-salon',
  'legal-firm',
  'accounting',
  'construction',
  'photography',
  'wedding-events',
  'pet-services',
  'cleaning-services',
  'landscaping',
  'hvac-plumbing',
  'electrical',
  'roofing',
  'moving-storage',
  'dental-clinic',
  'veterinary',
  'daycare',
  'tutoring',
  'music-lessons',
  'martial-arts',
  'yoga-studio',
  'spa-massage',
  'barbershop',
  'florist',
  'bakery',
  'coffee-shop',
  'food-truck',
  'catering',
  'brewery-winery',
  'hotel-bnb',
  'travel-agency',
  'insurance',
  'financial-advisor',
  'it-services',
  'marketing-agency',
  'consulting',
  'coaching',
  'nonprofit',
  'church-religious',
]);
export type TemplateType = z.infer<typeof TemplateType>;

export const SeoSchema = z.object({
  title: z.string(),
  description: z.string(),
});
export type Seo = z.infer<typeof SeoSchema>;

export const TestimonialSchema = z.object({
  name: z.string(),
  role: z.string().optional(),
  content: z.string(),
  rating: z.number().min(1).max(5).optional(),
});
export type Testimonial = z.infer<typeof TestimonialSchema>;

export const ServiceSchema = z.object({
  name: z.string(),
  description: z.string(),
  icon: z.string().optional(),
});
export type Service = z.infer<typeof ServiceSchema>;

export const FaqItemSchema = z.object({
  question: z.string(),
  answer: z.string(),
});
export type FaqItem = z.infer<typeof FaqItemSchema>;

export const ServiceWithPriceSchema = z.object({
  name: z.string(),
  description: z.string(),
  icon: z.string().optional(),
  price: z.string().optional(),
});
export type ServiceWithPrice = z.infer<typeof ServiceWithPriceSchema>;

export const SiteContentSchema = z.object({
  headline: z.string(),
  aboutText: z.string(),
  services: z.array(ServiceWithPriceSchema),
  testimonials: z.array(TestimonialSchema),
  features: z.record(z.any()).optional(),
  seo: SeoSchema,
  faq: z.array(FaqItemSchema).optional(),
  videoUrl: z.string().optional(),
});
export type SiteContent = z.infer<typeof SiteContentSchema>;

// ---------------------------------------------------------------------------
// HTML-first generation output (new system)
// ---------------------------------------------------------------------------

export const SiteMetaSchema = z.object({
  templateId: z.string(),
  sitePersonality: z.string(),
  primaryAngle: z.string(),
  sectionsUsed: z.array(z.string()),
});
export type SiteMeta = z.infer<typeof SiteMetaSchema>;

export const GeneratedSiteSchema = z.object({
  meta: SiteMetaSchema,
  html: z.string(),
});
export type GeneratedSite = z.infer<typeof GeneratedSiteSchema>;

export const SeoExtractedSchema = z.object({
  title: z.string(),
  description: z.string(),
});
export type SeoExtracted = z.infer<typeof SeoExtractedSchema>;

export const BusinessDataSchema = z.object({
  phone: z.string().nullable().optional(),
  rating: z.number().nullable().optional(),
  reviewCount: z.number().default(0),
  businessHours: z.array(z.string()).default([]),
  photos: z.array(z.string()).default([]),
  googleMapsUrl: z.string().nullable().optional(),
  address: z.string().optional(),
  googleReviews: z.array(GoogleReviewSchema).optional(),
});
export type BusinessData = z.infer<typeof BusinessDataSchema>;

export const CustomThemeSchema = z.object({
  primaryColor: z.string(),
  accentColor: z.string(),
  bgColor: z.string(),
  textColor: z.string(),
  bodyTextColor: z.string().optional(),
  // Font settings
  headingFont: z.string().optional(),
  bodyFont: z.string().optional(),
});
export type CustomTheme = z.infer<typeof CustomThemeSchema>;

export const SiteSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  leadId: z.string(),
  name: z.string(),
  templateType: TemplateType,
  status: SiteStatus,
  views: z.number().default(0),
  // Legacy structured content (kept for backward compatibility)
  content: SiteContentSchema,
  // HTML-first generation output (new system)
  generatedHtml: z.string().optional(),
  siteMeta: SiteMetaSchema.optional(),
  businessData: BusinessDataSchema.optional(),
  selectedFeatures: z.array(z.string()).optional(),
  selectedLayouts: z.array(z.string()).optional(),
  previewHtml: z.string().optional(),
  previewToken: z.string().optional(),
  price: z.number().optional(), // Price in cents (e.g., 29900 = $299.00)
  customTheme: CustomThemeSchema.optional(),
  createdAt: z.any(),
  updatedAt: z.any(),
  soldAt: z.any().optional(),
});
export type Site = z.infer<typeof SiteSchema>;

export const ScrapingSessionSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  campaignId: z.string(),
  status: z.enum(['pending', 'in_progress', 'completed', 'failed']),
  totalFound: z.number().default(0),
  leadsCreated: z.number().default(0),
  leadsSkipped: z.number().default(0),
  errors: z.array(z.string()).default([]),
  startedAt: z.any(),
  completedAt: z.any().optional(),
});
export type ScrapingSession = z.infer<typeof ScrapingSessionSchema>;

export const OutreachEmailStatus = z.enum(['pending', 'sent', 'delivered', 'opened', 'clicked', 'replied', 'bounced', 'failed']);
export type OutreachEmailStatus = z.infer<typeof OutreachEmailStatus>;

export const OutreachEmailSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  leadId: z.string(),
  siteId: z.string().optional(),
  to: z.string(),
  subject: z.string(),
  body: z.string(),
  status: OutreachEmailStatus,
  sentAt: z.any().optional(),
  openedAt: z.any().optional(),
  clickedAt: z.any().optional(),
  createdAt: z.any(),
});
export type OutreachEmail = z.infer<typeof OutreachEmailSchema>;


export const OrderSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  siteId: z.string(),
  amount: z.number(),
  currency: z.string().default('usd'),
  status: z.enum(['pending', 'completed', 'refunded']),
  stripePaymentIntentId: z.string().optional(),
  createdAt: z.any(),
});
export type Order = z.infer<typeof OrderSchema>;

export const CommissionSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  orderId: z.string(),
  siteId: z.string(),
  amount: z.number(),
  rate: z.number(),
  status: z.enum(['pending', 'approved', 'paid']),
  createdAt: z.any(),
  paidAt: z.any().optional(),
});
export type Commission = z.infer<typeof CommissionSchema>;

export const PayoutSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  amount: z.number(),
  currency: z.string().default('usd'),
  status: z.enum(['pending', 'processing', 'completed', 'failed']),
  stripePayoutId: z.string().optional(),
  commissionIds: z.array(z.string()),
  createdAt: z.any(),
  completedAt: z.any().optional(),
});
export type Payout = z.infer<typeof PayoutSchema>;


export const PLAN_CONFIGS: Record<PlanId, { name: string; price: number; quotas: Quotas; features: string[]; tagline: string; popular?: boolean; oneTime?: boolean; trialDays?: number; annualPrice?: number }> = {
  // Billed once, never renews. `oneTime` is what the UI reads to print "once"
  // instead of "/mo", and what the quota copy reads to avoid promising a
  // monthly reset that will never come.
  lifetime: {
    name: 'Lifetime',
    // $79 as of 2026-07-24 (was $99). New Stripe price
    // price_1TweNvFpon0xkMQFQ1Mt1hY5 on the same "Romulus: Lifetime Plan"
    // product; the old $99 price stays active so the two existing buyers keep
    // resolving. Sold on the MOBILE paywall only.
    price: 79,
    tagline: 'Pay once. Keep it forever.',
    oneTime: true,
    quotas: { leadsLimit: 1000, sitesLimit: 500 },
    features: [
      '1,000 leads — one time, no reset',
      '500 generated websites',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
  // Same product, same allowance, lower price. Reached only from /offer.
  lifetime_offer: {
    name: 'Lifetime',
    // $59 one-time. Stripe price price_1TwfEXFpon0xkMQFdEb7axr7 on its own
    // product "Romulus: Lifetime (Exit Offer)" - deliberately NOT the $79
    // product, so the exit offer's take is separable in the dashboard.
    price: 59,
    tagline: 'Pay once. Keep it forever.',
    oneTime: true,
    quotas: { leadsLimit: 1000, sitesLimit: 500 },
    features: [
      '1,000 leads — one time, no reset',
      '500 generated websites',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
  free: {
    name: 'Free',
    price: 0,
    tagline: 'Grandfathered legacy access.',
    quotas: { leadsLimit: 25, sitesLimit: 5 },
    features: [
      '25 leads (one-time)',
      '5 generated websites (one-time)',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
  // Mobile-only. The paywall's subscription option and Lifetime's price anchor:
  // the whole screen is built on $99-once vs $49-every-month, a ~13x leads
  // ratio, and "$588 a year" — all computed from this entry. Numbers here are
  // load-bearing for that arithmetic; they do NOT move when the desktop ladder
  // reprices. No trialDays on purpose (mobile charges immediately), no
  // annualPrice (the toggle is a desktop-only surface).
  monthly: {
    name: 'Monthly',
    price: 49,
    tagline: 'Keep it small. Cancel anytime.',
    quotas: { leadsLimit: 75, sitesLimit: 12 },
    features: [
      '75 leads per month',
      '12 generated websites',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
  starter: {
    name: 'Starter',
    price: 49,
    tagline: 'Close your first deal.',
    quotas: { leadsLimit: 75, sitesLimit: 12 },
    features: [
      '75 leads per month',
      '12 generated websites',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
  cashflow: {
    name: 'Cashflow',
    price: 149,
    tagline: 'Turn this into consistent income.',
    popular: true,
    quotas: { leadsLimit: 300, sitesLimit: 60 },
    features: [
      '300 leads per month',
      '60 generated websites',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
  scale: {
    name: 'Scale',
    price: 599,
    tagline: 'Remove the ceiling.',
    quotas: { leadsLimit: 2000, sitesLimit: 250 },
    features: [
      '2,000 leads per month',
      '250 generated websites',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
  // Retired 2026-07-12, restored 2026-07-24 with the legacy funnel. Worth
  // remembering why it went, since the reasoning still stands: zero checkouts
  // were ever started on it in the product's history, and the "keep a high tier
  // to anchor the middle one" theory it existed for does not survive replication
  // — Huber, Payne & Puto, who authored the 1982 decoy paper, wrote in 2014 that
  // they "suspect the asymmetric dominance effect occurs rarely in the
  // marketplace today." It is back because the restored grid lists it, not
  // because the anchor argument got better.
  agency: {
    name: 'Agency',
    price: 1299,
    tagline: 'Dominate your market.',
    quotas: { leadsLimit: 10000, sitesLimit: 1000 },
    features: [
      '10,000 leads per month',
      '1,000 generated websites',
      'All templates',
      '100% of every sale',
      'Email & SMS outreach',
    ],
  },
};

// NO PLAN CARRIES A FREE TRIAL. Removed 2026-07-13: every desktop plan now charges
// on signup. `trialDays` and planHasTrial() survive only as the guard rail that keeps
// the trial code paths dead — nothing sets the field, so every `hasTrial` branch in
// the UI and both Stripe functions resolve to the pay-now side.
//
// Why it went, and why re-adding it needs a deliberate decision rather than one line:
// the trial was card-OPTIONAL in practice. createGuestSubscription() creates the
// subscription with trial_period_days BEFORE the card is confirmed, so Stripe puts it
// in `trialing` immediately — and isPaidStatus() (stripe/index.ts) counts `trialing`
// as entitled. Anyone who merely opened the checkout form got 7 days of full lead
// access and then cancelled themselves via missing_payment_method:'cancel', never
// paying and never being contactable. Four such subscriptions existed on the live
// account the day this was removed, none with a payment method attached.
//
// If a trial ever comes back: a trialing subscription has NO invoice to pay, so Stripe
// returns a pending_setup_intent instead of latest_invoice.payment_intent. Verified
// against API version 2024-06-20 (the version this app pins). Any code that reaches
// for the PaymentIntent on a trial plan gets undefined and renders no payment form at
// all — exactly how the lifetime SKU shipped broken. The client must call
// stripe.confirmSetup(), not stripe.confirmPayment(). And entitlement must NOT be
// granted until that SetupIntent is confirmed, or the hole above reopens.

// Monthly vs annual. Annual is 10x the monthly price: two months free, 16.7% off.
// That is the actual market convention, not a number I picked — Ahrefs' own pricing
// page embeds a JSON blob where every annual SKU is exactly 10x its monthly one.
//
// Neither interval carries a trial any more (see above). Both pay on signup and lean
// on the 30-day guarantee in ToS 7.3 instead.
export type BillingInterval = 'month' | 'year';

export function annualSavingsPercent(planId: PlanId): number {
  const p = PLAN_CONFIGS[planId];
  if (!p?.annualPrice || !p.price) return 0;
  return Math.round((1 - p.annualPrice / (p.price * 12)) * 100);
}

// What we print on the toggle: the per-month figure someone actually pays when
// billed yearly. Never invent a "was" price to sit next to it.
export function effectiveMonthly(planId: PlanId, interval: BillingInterval): number {
  const p = PLAN_CONFIGS[planId];
  if (interval === 'year' && p?.annualPrice) return Math.round(p.annualPrice / 12);
  return p?.price ?? 0;
}

export function planHasAnnual(planId: PlanId): boolean {
  return !!PLAN_CONFIGS[planId]?.annualPrice;
}

// The saving in DOLLARS, not as a per-month delta.
//
// At the conventional "two months free" (16.7%) the toggle moved $99 to $83, which
// reads as nothing and is why nobody would have taken it. The convention is calibrated
// for a business with healthy retention, where a deep annual discount gives away
// revenue you would have collected anyway. This one churns ~89% at 1.13 payments per
// customer, so there is no future revenue to give away: an annual sale at 30% off is
// roughly 8x actual LTV. Hence 30%, and hence saying "$900" instead of "17%".
export function annualSavingsDollars(planId: PlanId): number {
  const p = PLAN_CONFIGS[planId];
  if (!p?.annualPrice || !p.price) return 0;
  return p.price * 12 - p.annualPrice;
}

export function planHasTrial(planId: PlanId | undefined | null): boolean {
  if (!planId) return false;
  return !!PLAN_CONFIGS[planId]?.trialDays;
}

export const NICHES = [
  'Plumber',
  'Electrician',
  'HVAC',
  'Roofing',
  'Landscaping',
  'Cleaning Services',
  'Auto Repair',
  'Dentist',
  'Chiropractor',
  'Law Firm',
  'Accounting',
  'Real Estate',
  'Restaurant',
  'Salon',
  'Gym',
  'Yoga Studio',
  'Photography',
  'Wedding Planner',
  'Pet Grooming',
  'Veterinarian',
  'Daycare',
  'Tutoring',
  'Music Lessons',
  'Martial Arts',
  'Massage Therapy',
  'Physical Therapy',
  'Home Improvement',
  'Painting',
  'Flooring',
  'Moving Company',
] as const;

export type GenerateSiteContentRequest = {
  leadId: string;
  templateType: TemplateType;
  selectedFeatures?: string[];
  selectedLayouts?: string[];
};

export type GenerateSiteContentResponse = {
  siteId: string;
  content: SiteContent;
  /** Full generated HTML (new system) */
  generatedHtml?: string;
  /** Site meta (new system) */
  siteMeta?: SiteMeta;
};

export type ScrapeGooglePlacesRequest = {
  campaignId: string;
  query: string;
  location: string;
  radius?: number;
};

export type ScrapeGooglePlacesResponse = {
  sessionId: string;
  leadsCreated: number;
  leadsSkipped: number;
};

export type CreateCheckoutRequest = {
  planId: PlanId;
  successUrl: string;
  cancelUrl: string;
};

export type CreateCheckoutResponse = {
  sessionUrl: string;
};

export type SendOutreachEmailRequest = {
  leadId: string;
  siteId?: string;
  templateId?: string;
  customSubject?: string;
  customBody?: string;
};

export type SendOutreachEmailResponse = {
  emailId: string;
  status: OutreachEmailStatus;
};

// SendBusinessEmail
export type SendBusinessEmailRequest = {
  businessEmail: string;
  businessName?: string;
  previewUrl: string;
  campaignId?: string;
  leadId?: string;
  siteId?: string;
  customSubject?: string;
  customBody?: string;
};

export type SendBusinessEmailResponse = {
  status: 'sent';
  emailId?: string;
  message?: string;
};

// Unified business outreach (email or SMS)
export type SendBusinessOutreachRequest = {
  leadId: string;
  siteId: string;
  previewUrl: string;
  contactMethod: 'email' | 'sms';
  // For email
  businessEmail?: string;
  customSubject?: string;
  customBody?: string;
  // For SMS
  businessPhone?: string;
  customMessage?: string;
  // Shared
  businessName?: string;
  campaignId?: string;
};

export type SendBusinessOutreachResponse = {
  status: 'sent';
  method: 'email' | 'sms';
  messageId?: string;
  message?: string;
};
